import axios from "../helpers/axios";
import type {
  CampaignUserSortOption,
  TimeUnit,
} from "../pages/admin/email-campaign/types";

export interface ReengagementSequenceStepApi {
  _id: string;
  step_name: string;
  offsetValue: number;
  offsetUnit: TimeUnit;
  timeToSend?: string;
  prompt: string;
}

export type CharacterStrategyApi =
  | "last-character"
  | "character-with-most-conversation";

export interface ReengagementCharacterSelectionApi {
  tokenThreshold?: number;
  characterStrategy?: CharacterStrategyApi;
  default_fallback_character_id?: string;
}

export interface ReengagementCharacterSelectionRequest {
  tokenThreshold: number;
  characterStrategy: CharacterStrategyApi;
  default_fallback_character_id: string;
}

export interface CharacterReplyTimingApi {
  dynamicRange?: {
    minTime?: number;
    maxTime?: number;
    unit?: "hours" | "minutes";
    // Legacy support
    minHours?: number;
    maxHours?: number;
  };
}

export interface CharacterReplyTimingRequest {
  dynamicRange: {
    minTime: number;
    maxTime: number;
    unit?: "hours" | "minutes";
  };
}

export interface NoChatIntroScheduleApi {
  offsetValue: number;
  offsetUnit: TimeUnit;
  timeToSend?: string;
}

export interface NoChatIntroScheduleRequest {
  offsetValue: number;
  offsetUnit: TimeUnit;
  timeToSend?: string;
}

export interface NoChatIntroScenarioApi {
  _id: string;
  storylineTitle: string;
  storylineSummary?: string;
  characterId: string;
  fromCharacterId?: string;
  from_character_id?: string;
  subject: string;
  body: string;
  prompts?: string[];
  updatedAt?: string;
  createdAt?: string;
  maxConversationCount?: number | null;
  promptInstructions?: string;
  manualEmailEnabled?: boolean;
  manualEmailSubject?: string;
  manualEmailBody?: string;
}

export interface NoChatIntroApi {
  schedule?: NoChatIntroScheduleApi;
  scenarios?: NoChatIntroScenarioApi[];
  triggerScenarios?: NoChatIntroScenarioApi[];
}

export interface ReachOutRuleSendWindowApi {
  offsetValue: number;
  offsetUnit: TimeUnit;
  timeToSend?: string;
}

export interface ReachOutCharacterRuleApi {
  _id: string;
  id: string;
  reachoutCharacterId: string;
  reachoutCharacterTier?: string;
  triggerCharacterIds?: string[];
  minimumTokens: number;
  triggerAfterAnyCount?: number;
  resendCooldownDays?: number;
  sendWindow?: ReachOutRuleSendWindowApi;
  promptInstructions?: string;
  manualEmailEnabled?: boolean;
  manualEmailSubject?: string;
  manualEmailBody?: string;
}

export interface ReachOutCharacterRulesApi {
  characterRules?: ReachOutCharacterRuleApi[];
}

export interface NoChatIntroScenarioRequest {
  storylineTitle: string;
  storylineSummary?: string;
  characterId: string;
  fromCharacterId?: string;
  from_character_id?: string;
  subject: string;
  body: string;
  prompts?: string[];
  maxConversationCount?: number | null;
  promptInstructions?: string;
  manualEmailEnabled?: boolean;
  manualEmailSubject?: string;
  manualEmailBody?: string;
}

export interface ReachOutCharacterRuleRequest {
  id: string;
  reachoutCharacterId: string;
  reachoutCharacterTier?: string;
  triggerCharacterIds?: string[];
  minimumTokens: number;
  triggerAfterAnyCount?: number;
  resendCooldownDays?: number;
  sendWindow: ReachOutRuleSendWindowApi;
  promptInstructions?: string;
  manualEmailEnabled?: boolean;
  manualEmailSubject?: string;
  manualEmailBody?: string;
}

export interface CampaignApiModel {
  _id: string;
  name: string;
  active: boolean;
  reengagementEmailSequence?: ReengagementSequenceStepApi[];
  reengagementCharacterSelection?: ReengagementCharacterSelectionApi;
  characterReplyTiming?: CharacterReplyTimingApi;
  noChatIntro?: NoChatIntroApi;
  reachOutCharacterRules?: ReachOutCharacterRulesApi;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignSummaryApi {
  _id: string;
  name: string;
  active: boolean;
}

export interface CampaignUserCharacterApi {
  characterId: string;
  characterName: string;
  tokens: number;
}

export interface CampaignUserApi {
  userId: string;
  name?: string;
  email: string;
  lastActive?: string;
  totalConversations?: number;
  totalTokens?: number;
  isActivelyPaying?: boolean;
  membershipBadge?: string;
  ranOutOfTokens?: boolean;
  everRanOutOfTokens?: boolean;
  tokenBadge?: string;
  characters?: CampaignUserCharacterApi[];
  campaigns?: Array<{
    campaignId: string;
    campaignName: string;
    lastSentAt?: string;
  }>;
}

interface CampaignListResponse {
  campaigns: CampaignSummaryApi[];
}

interface CampaignResponse {
  campaign: CampaignApiModel;
}

interface CampaignUsersResponse {
  users: CampaignUserApi[];
}

export interface DuplicateCampaignRequest {
  name: string;
  sections: {
    sequence: boolean;
    characterSelection: boolean;
    replyTiming: boolean;
    noChatIntro: boolean;
    premiumReachouts: boolean;
  };
}

export type ManualTriggerType = "reengagement" | "no_chat_intro" | "reachout";

export interface ManualTriggerRequestEntry {
  userId: string;
  triggers: ManualTriggerType[];
}

export interface ManualTriggerResult {
  trigger: ManualTriggerType;
  status: string;
  scheduled_for?: string;
  jobId?: string;
}

export interface ManualTriggerResultEntry {
  userId: string;
  triggers: ManualTriggerResult[];
}

export interface ManualTriggerResponse {
  campaign_id: string;
  count: number;
  results: ManualTriggerResultEntry[];
}

export interface CampaignMetric {
  campaignId: string;
  campaignName: string;
  features?: {
    reengagementSequence?: boolean;
    noChatIntro?: boolean;
    reachout?: boolean;
    emailReply?: boolean;
    [key: string]: boolean | undefined;
  };
  audienceCount: number;
  sent: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
  replied: number;
  openRate: number;
  clickRate: number;
  unsubscribeRate: number;
  replyRate: number;
}

export interface SentEmailRecipientApi {
  userId: string;
  email: string;
  totalEmails: number;
  threadCount: number;
  campaignCount: number;
  lastSentAt?: string;
  openedCount?: number;
  repliedCount?: number;
  hasOpened?: boolean;
  hasReplied?: boolean;
  lastOpenedAt?: string;
  lastRepliedAt?: string;
  campaigns?: Array<{
    campaignId: string;
    campaignName?: string;
    sent?: number;
    opened?: number;
    replied?: number;
    lastSentAt?: string;
    lastOpenedAt?: string | null;
    lastRepliedAt?: string | null;
  }>;
}

interface SentEmailRecipientsPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SentEmailRecipientsResponse {
  search?: string;
  pagination: SentEmailRecipientsPagination;
  users: SentEmailRecipientApi[];
}

export interface SentEmailEventApi {
  type: string;
  timestamp: string;
  metadata?: Record<string, string>;
}

export interface SentEmailThreadEntryApi {
  role: string;
  content: string;
  timestamp: string;
}

export interface SentEmailTimelineEmailApi {
  emailId: string;
  campaignId?: string;
  campaignName?: string;
  characterName?: string;
  subject?: string;
  sequenceIndex?: number;
  sentAt: string;
  sentAtLocal?: string;
  latestStatus?: string;
  events: SentEmailEventApi[];
  thread: SentEmailThreadEntryApi[];
  metadata?: Record<string, string>;
}

interface SentEmailTimelinePagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SentEmailTimelineResponse {
  userId: string;
  timezone: string;
  pagination: SentEmailTimelinePagination;
  emails: SentEmailTimelineEmailApi[];
}

export interface FetchSentEmailRecipientsParams {
  search?: string;
  limit?: number;
  page?: number;
  campaignId?: string;
  status?: "opened" | "replied" | "opened_replied" | "sent" | string;
}

export interface FetchUserSentEmailTimelineParams {
  limit?: number;
  page?: number;
  campaignId?: string;
  timezone?: string;
}

export type ScheduledEmailJobTypeApi = "sequence" | "reachout" | "no-chat-intro" | "email-reply";

export type ScheduledEmailStatusApi =
  | "pending"
  | "executing"
  | "completed"
  | "failed"
  | "cancelled"
  | "sent";

export interface ScheduledEmailApi {
  id: string;
  userId: string;
  email: string;
  createdAt?: string;
  userTimezone?: string;
  scheduledFor: string;
  scheduledForLocal?: string;
  jobType: ScheduledEmailJobTypeApi;
  sequenceIndex?: number;
  status: ScheduledEmailStatusApi;
  emailSubject?: string;
  campaignId?: string;
  campaignName?: string;
}

interface ScheduledEmailsPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ScheduledEmailsResponse {
  campaign_id: string;
  timezone: string;
  pagination: ScheduledEmailsPagination;
  results: ScheduledEmailApi[];
}

export interface FetchScheduledEmailsParams {
  status?: string | string[];
  jobType?: string | string[];
  limit?: number;
  page?: number;
  timezone?: string;
}

export interface FetchCampaignUsersParams {
  sort?: CampaignUserSortOption;
  paidUsersOnly?: boolean;
  ranOutOfTokensOnly?: boolean;
  everRanOutOfTokensOnly?: boolean;
  minTokens?: number;
  maxTokens?: number;
  fromDate?: string;
  toDate?: string;
  characterIds?: string[];
  minUniqueCharacters?: number;
  maxConversationCount?: number;
}

export const fetchCampaignSummaries = async (): Promise<CampaignSummaryApi[]> => {
  const response = await axios.get<CampaignListResponse>("/api/v1/campaigns");
  return response.data.campaigns ?? [];
};

export const fetchCampaignById = async (
  id: string
): Promise<CampaignApiModel> => {
  const response = await axios.get<CampaignResponse>(`/api/v1/campaigns/${id}`);
  return response.data.campaign;
};

export const createCampaign = async (
  name: string
): Promise<CampaignApiModel> => {
  const response = await axios.post<CampaignResponse>("/api/v1/campaigns", {
    name,
  });
  return response.data.campaign;
};

export const duplicateCampaign = async (
  sourceCampaignId: string,
  payload: DuplicateCampaignRequest
): Promise<CampaignApiModel> => {
  const response = await axios.post<CampaignResponse>(
    `/api/v1/campaigns/${sourceCampaignId}/duplicate`,
    payload
  );
  return response.data.campaign;
};

export const activateCampaign = async (
  id: string
): Promise<CampaignApiModel> => {
  const response = await axios.patch<CampaignResponse>(
    `/api/v1/campaigns/${id}/activate`,
    {}
  );
  return response.data.campaign;
};

export const deactivateCampaign = async (
  id: string
): Promise<CampaignApiModel> => {
  const response = await axios.patch<CampaignResponse>(
    `/api/v1/campaigns/${id}/deactivate`,
    {}
  );
  return response.data.campaign;
};

interface DeleteCampaignResponse {
  message: string;
  id: string;
}

export const deleteCampaign = async (id: string): Promise<DeleteCampaignResponse> => {
  const response = await axios.delete<DeleteCampaignResponse>(
    `/api/v1/campaigns/${id}`
  );
  return response.data;
};

export const fetchCampaignUsers = async (
  params?: FetchCampaignUsersParams
): Promise<CampaignUserApi[]> => {
  const response = await axios.get<CampaignUsersResponse>(
    "/api/v1/campaigns/users",
    { params }
  );
  return response.data.users ?? [];
};

export const fetchSentEmailRecipients = async (
  params?: FetchSentEmailRecipientsParams
): Promise<SentEmailRecipientsResponse> => {
  const response = await axios.get<SentEmailRecipientsResponse>(
    "/api/v1/campaigns/sent-emails/users",
    { params }
  );
  return response.data;
};

export const fetchUserSentEmailTimeline = async (
  userId: string,
  params?: FetchUserSentEmailTimelineParams
): Promise<SentEmailTimelineResponse> => {
  const response = await axios.get<SentEmailTimelineResponse>(
    `/api/v1/campaigns/sent-emails/users/${userId}`,
    { params }
  );
  return response.data;
};

export const fireManualCampaignTriggers = async (
  campaignId: string,
  payload: ManualTriggerRequestEntry[]
): Promise<ManualTriggerResponse> => {
  const response = await axios.post<ManualTriggerResponse>(
    `/api/v1/campaigns/${campaignId}/manual-triggers`,
    payload
  );
  return response.data;
};

export const fetchScheduledEmails = async (
  campaignId: string,
  params?: FetchScheduledEmailsParams
): Promise<ScheduledEmailsResponse> => {
  const response = await axios.get<ScheduledEmailsResponse>(
    `/api/v1/campaigns/${campaignId}/scheduled-emails`,
    { params }
  );
  return response.data;
};

interface CancelScheduledEmailResponse {
  message: string;
  job: ScheduledEmailApi;
}

export const cancelScheduledEmail = async (
  campaignId: string,
  scheduledEmailId: string
): Promise<CancelScheduledEmailResponse> => {
  const response = await axios.delete<CancelScheduledEmailResponse>(
    `/api/v1/campaigns/${campaignId}/scheduled-emails/${scheduledEmailId}`
  );
  return response.data;
};

export const fetchCampaignMetrics = async (
  campaignIds?: string[]
): Promise<CampaignMetric[]> => {
  const params =
    campaignIds && campaignIds.length
      ? { campaignIds: campaignIds.join(",") }
      : undefined;
  const response = await axios.get<CampaignMetric[]>(
    "/api/v1/campaigns/metrics",
    { params }
  );
  return Array.isArray(response.data) ? response.data : [];
};

interface ReengagementCharacterSelectionResponse {
  reengagementCharacterSelection: ReengagementCharacterSelectionApi;
}

export const updateReengagementCharacterSelection = async (
  campaignId: string,
  payload: ReengagementCharacterSelectionRequest
): Promise<ReengagementCharacterSelectionApi> => {
  const response = await axios.patch<ReengagementCharacterSelectionResponse>(
    `/api/v1/campaigns/${campaignId}/reengagement-character-selection`,
    payload
  );
  return response.data.reengagementCharacterSelection;
};

interface CharacterReplyTimingResponse {
  characterReplyTiming: CharacterReplyTimingApi;
}

export const updateCharacterReplyTiming = async (
  campaignId: string,
  payload: CharacterReplyTimingRequest
): Promise<CharacterReplyTimingApi> => {
  const response = await axios.patch<CharacterReplyTimingResponse>(
    `/api/v1/campaigns/${campaignId}/character-reply-timing`,
    payload
  );
  return response.data.characterReplyTiming;
};

interface NoChatScheduleResponse {
  schedule: NoChatIntroScheduleApi;
}

export const updateNoChatSchedule = async (
  campaignId: string,
  payload: NoChatIntroScheduleRequest
): Promise<NoChatIntroScheduleApi> => {
  const response = await axios.patch<NoChatScheduleResponse>(
    `/api/v1/campaigns/${campaignId}/no-chat-intro/schedule`,
    payload
  );
  return response.data.schedule;
};

interface NoChatScenarioResponse {
  scenario: NoChatIntroScenarioApi;
}

export const addNoChatScenario = async (
  campaignId: string,
  payload: NoChatIntroScenarioRequest
): Promise<NoChatIntroScenarioApi> => {
  const response = await axios.post<NoChatScenarioResponse>(
    `/api/v1/campaigns/${campaignId}/no-chat-intro/scenarios`,
    payload
  );
  return response.data.scenario;
};

export const updateNoChatScenario = async (
  campaignId: string,
  scenarioId: string,
  payload: Partial<NoChatIntroScenarioRequest>
): Promise<NoChatIntroScenarioApi> => {
  const response = await axios.patch<NoChatScenarioResponse>(
    `/api/v1/campaigns/${campaignId}/no-chat-intro/scenarios/${scenarioId}`,
    payload
  );
  return response.data.scenario;
};

export const deleteNoChatScenario = async (
  campaignId: string,
  scenarioId: string
): Promise<void> => {
  await axios.delete(
    `/api/v1/campaigns/${campaignId}/no-chat-intro/scenarios/${scenarioId}`
  );
};

interface ReachOutRuleResponse {
  rule: ReachOutCharacterRuleApi;
}

export const addReachOutRule = async (
  campaignId: string,
  payload: ReachOutCharacterRuleRequest
): Promise<ReachOutCharacterRuleApi> => {
  const response = await axios.post<ReachOutRuleResponse>(
    `/api/v1/campaigns/${campaignId}/reachout-rules`,
    payload
  );
  return response.data.rule;
};

export const updateReachOutRule = async (
  campaignId: string,
  ruleId: string,
  payload: Partial<ReachOutCharacterRuleRequest>
): Promise<ReachOutCharacterRuleApi> => {
  const response = await axios.patch<ReachOutRuleResponse>(
    `/api/v1/campaigns/${campaignId}/reachout-rules/${ruleId}`,
    payload
  );
  return response.data.rule;
};

export const deleteReachOutRule = async (
  campaignId: string,
  ruleId: string
): Promise<void> => {
  await axios.delete(
    `/api/v1/campaigns/${campaignId}/reachout-rules/${ruleId}`
  );
};

interface SequenceStepRequestBody {
  step_name?: string;
  offsetValue?: number;
  offsetUnit?: TimeUnit;
  timeToSend?: string;
  prompt?: string;
}

interface SequenceStepResponse {
  step: ReengagementSequenceStepApi;
}

export const addReengagementSequenceStep = async (
  campaignId: string,
  payload: SequenceStepRequestBody
): Promise<ReengagementSequenceStepApi> => {
  const response = await axios.post<SequenceStepResponse>(
    `/api/v1/campaigns/${campaignId}/reengagement-sequence`,
    payload
  );
  return response.data.step;
};

export const updateReengagementSequenceStep = async (
  campaignId: string,
  stepId: string,
  payload: SequenceStepRequestBody
): Promise<ReengagementSequenceStepApi> => {
  const response = await axios.patch<SequenceStepResponse>(
    `/api/v1/campaigns/${campaignId}/reengagement-sequence/${stepId}`,
    payload
  );
  return response.data.step;
};

export const deleteReengagementSequenceStep = async (
  campaignId: string,
  stepId: string
): Promise<void> => {
  await axios.delete(
    `/api/v1/campaigns/${campaignId}/reengagement-sequence/${stepId}`
  );
};
